import { useEffect, useMemo, useCallback } from 'react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { getBrandDefaultCurrency } from '../../shared/utils/brandCurrency';
import {
  DEFAULT_COMPTABILITE_CONFIG,
  normalizeComptabiliteConfig,
  buildComptabiliteEntityPrefixes,
} from './comptabiliteConfigDefaults';

export { DEFAULT_COMPTABILITE_CONFIG, normalizeComptabiliteConfig, getComptabiliteEntityPrefix } from './comptabiliteConfigDefaults';

let hydratePromise = null;

function hydrateComptabiliteConfig(patchSettings, existingSettings) {
  if (!hydratePromise) {
    hydratePromise = localApiAdapter
      .get('/comptabilite/module-config')
      .then((response) => {
        const raw = response?.data !== undefined ? response.data : response;
        if (raw && typeof raw === 'object') {
          syncComptabiliteConfigToSettings(raw, patchSettings, existingSettings);
        }
        return normalizeComptabiliteConfig(raw || {});
      })
      .catch(() => {
        hydratePromise = null;
        return normalizeComptabiliteConfig({ defaultCurrency: getBrandDefaultCurrency() });
      });
  }
  return hydratePromise;
}

export function syncComptabiliteConfigToSettings(config, patchSettings, existingSettings = {}) {
  const normalized = normalizeComptabiliteConfig({
    ...config,
    defaultCurrency: config.defaultCurrency || getBrandDefaultCurrency(),
  });
  const entityPrefixes = buildComptabiliteEntityPrefixes(normalized);
  const existingConfig = existingSettings?.configuration || {};

  patchSettings(
    {
      accounting: { ...normalized, _syncedFromApi: true },
      configuration: {
        entityPrefixes: {
          ...(existingConfig.entityPrefixes || {}),
          ...entityPrefixes,
        },
      },
    },
    { persist: false },
  );
  return normalized;
}

/** Live comptabilité module config — hydrated from API, then SettingsContext. */
export function useComptabiliteConfig() {
  const { settings, patchSettings } = useSettings();

  useEffect(() => {
    if (settings?.accounting?._syncedFromApi) return;
    void hydrateComptabiliteConfig(patchSettings, settings);
  }, [settings?.accounting?._syncedFromApi, patchSettings, settings]);

  const config = useMemo(
    () =>
      normalizeComptabiliteConfig({
        ...(settings?.accounting || {}),
        defaultCurrency: settings?.accounting?.defaultCurrency || getBrandDefaultCurrency(),
      }),
    [settings?.accounting],
  );

  const patchConfig = useCallback(
    (key, value) => {
      patchSettings({ accounting: { [key]: value } }, { persist: false });
      if (key === 'entityPrefixes' && value && typeof value === 'object') {
        const existingConfig = settings?.configuration || {};
        patchSettings(
          {
            configuration: {
              entityPrefixes: {
                ...(existingConfig.entityPrefixes || {}),
                ...buildComptabiliteEntityPrefixes({ ...config, entityPrefixes: value }),
              },
            },
          },
          { persist: false },
        );
      }
    },
    [patchSettings, settings?.configuration, config],
  );

  return { config, patchConfig, patchSettings };
}
